import type {
	FilterGroupKey,
	PropertyFilterGroup
} from '~/components/Filters/FiltersSidebar';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { PageName } from '~/presenters/PropertyFiltersPresenter';
import type { Breakpoint } from '~~/utils';

import { useBreakpoints } from '@vueuse/core';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { screenSizesInPixels } from '~~/utils';

import ConvinientSelect from '~/components/Filters/ConvinientSelect/Select.vue';
import MensalCondoPrice from '~/components/Filters/MensalCondoPrice/CondoPrice.vue';
import MeterSquarePrice from '~/components/Filters/MeterSquarePrice/SquarePrice.vue';
import PropertyArea from '~/components/Filters/PropertyArea/Area.vue';
import PropertyFeatureSelect from '~/components/Filters/PropertyFeatureSelect/FeatureSelect.vue';
import PropertyPrice from '~/components/Filters/PropertyPrice/Price.vue';
import PropertyStatus from '~/components/Filters/PropertyStatus/Status.vue';
import PropertyTypeSelect from '~/components/Filters/PropertyTypeSelect/TypeSelect.vue';
import RegionSelect from '~/components/Filters/RegionSelect/Select.vue';
import TaxPrice from '~/components/Filters/TaxPrice/Price.vue';

interface UsePropertyResponsiveFiltersParams {
	filters: Ref<PropertyFilters>;
	updateFilters: (filters: PropertyFilters, improveChanges?: boolean) => void;
	pageName: PageName;
}

interface UsePropertyResponsiveFiltersReturn {
	filtersBarOpenFilters: Ref<FilterGroupKey[]>;
	appliedFilterKeys: ComputedRef<(keyof PropertyFilters)[]>;
	filterComponents: ComputedRef<PropertyFilterGroup[]>;
	numberOfAppliedClosedFilters: ComputedRef<number>;
}

const openFiltersByScreenSize: Record<Breakpoint, FilterGroupKey[]> = {
	'xs-min': [],
	xs: [],
	'xs-large': [],
	sm: [],
	'sm-large': [],
	md: [],
	'md-large': [],
	lg: ['regions', 'pricing', 'area'],
	xl: ['regions', 'pricing', 'area', 'type'],
	'2xl': ['regions', 'pricing', 'area', 'type', 'rooms'],
	'3xl': ['regions', 'pricing', 'area', 'type', 'rooms', 'features']
};

function shouldShowAsOpenFilter(
	id: FilterGroupKey,
	openFilters: FilterGroupKey[]
): boolean {
	return openFilters.includes(id);
}

export function usePropertyResponsiveFilters({
	filters,
	updateFilters = () => Promise.resolve(),
	pageName
}: UsePropertyResponsiveFiltersParams): UsePropertyResponsiveFiltersReturn {
	const breakpoints = useBreakpoints(screenSizesInPixels);

	const filtersBarOpenFilters = computed<FilterGroupKey[]>(() => {
		if (breakpoints.smaller('lg').value) {
			return [];
		}

		const currentBreakpoint = breakpoints.active().value || 'sm';

		return openFiltersByScreenSize?.[currentBreakpoint as Breakpoint] || [];
	});

	const appliedFilterKeys = computed<(keyof PropertyFilters)[]>(() =>
		PropertyFiltersPresenter.getAppliedFilterKeys(filters.value)
	);
	const filterComponents = computed<PropertyFilterGroup[]>(() => [
		{
			id: 'regions',
			label: 'Bairros',
			keys: ['regions'],
			component: RegionSelect,
			extraProps: {
				regions: filters.value?.regions || [],
				city: filters.value?.cities?.[0],
				macroregion: filters.value?.macroregions?.[0],
				addressSlug: filters.value?.addressSlug,
				poiLocationLat: filters.value?.poiLocationLat,
				poiLocationLng: filters.value?.poiLocationLng,
				separatorVariant: 'white',
				'onUpdate-selection': (event: {
					regions: string[];
					states: string[];
					cities: string[];
					poiLocationLat: number;
					poiLocationLng: number;
				}) => {
					const currentFilters = {
						...filters.value,
						...event
					};

					const newPath = PropertyFiltersPresenter.getPathFromFilters(
						currentFilters,
						{
							regions: event.regions,
							cities: event.cities,
							states: event.states
						},
						{ page: pageName }
					);

					const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
						newPath,
						undefined,
						pageName
					);

					updateFilters({ ...newFilters, ...event });
				}
			},
			isOpen: shouldShowAsOpenFilter('regions', filtersBarOpenFilters.value),
			onUpdate: () => {}
		},
		{
			id: 'area',
			label: 'Área',
			keys: ['minArea', 'maxArea'],
			extraProps: {
				min: filters.value?.minArea,
				'onUpdate:min': (value: number) => {
					filters.value.minArea = value;
					updateFilters(filters.value);
				},
				max: filters.value?.maxArea,
				'onUpdate:max': (value: number) => {
					filters.value.maxArea = value;
					updateFilters(filters.value);
				}
			},
			component: PropertyArea,
			isOpen: shouldShowAsOpenFilter('area', filtersBarOpenFilters.value),
			onUpdate: () => {}
		},
		{
			id: 'pricing',
			label: 'Valor',
			keys: ['minAskingPrice', 'maxAskingPrice'],
			extraProps: {
				min: filters.value?.minAskingPrice,
				'onUpdate:min': (value: number) => {
					filters.value.minAskingPrice = value;
					updateFilters(filters.value);
				},
				max: filters.value?.maxAskingPrice,
				'onUpdate:max': (value: number) => {
					filters.value.maxAskingPrice = value;
					updateFilters(filters.value);
				}
			},
			component: PropertyPrice,
			isOpen: shouldShowAsOpenFilter('pricing', filtersBarOpenFilters.value),
			onUpdate: () => {}
		},
		{
			id: 'type',
			label: 'Tipo do imóvel',
			keys: ['propertyTypes'],
			extraProps: {
				modelValue: filters.value?.propertyTypes,
				separatorVariant: 'white'
			},
			component: PropertyTypeSelect,
			isOpen: shouldShowAsOpenFilter('type', filtersBarOpenFilters.value),
			onUpdate: value => {
				filters.value.propertyTypes = value;
				updateFilters(filters.value);
			}
		},
		{
			id: 'rooms',
			label: 'Cômodos',
			keys: ['bedrooms', 'suites', 'bathrooms', 'parkingSpots'],
			extraProps: {
				bedrooms: filters.value?.bedrooms,
				suites: filters.value?.suites,
				bathrooms: filters.value?.bathrooms,
				parkingSpots: filters.value?.parkingSpots,
				'onUpdate:bedrooms': (value: number) => {
					filters.value.bedrooms = value;
					updateFilters(filters.value);
				},
				'onUpdate:suites': (value: number) => {
					filters.value.suites = value;
					updateFilters(filters.value);
				},
				'onUpdate:bathrooms': (value: number) => {
					filters.value.bathrooms = value;
					updateFilters(filters.value);
				},
				'onUpdate:parkingSpots': (value: number) => {
					filters.value.parkingSpots = value;
					updateFilters(filters.value);
				}
			},
			component: ConvinientSelect,
			isOpen: shouldShowAsOpenFilter('rooms', filtersBarOpenFilters.value),
			onUpdate: () => {
				updateFilters(filters.value);
			}
		},
		{
			id: 'features',
			label: 'Características',
			keys: ['features'],
			extraProps: {
				modelValue: filters.value?.features,
				separatorVariant: 'white'
			},
			component: PropertyFeatureSelect,
			isOpen: shouldShowAsOpenFilter('features', filtersBarOpenFilters.value),
			onUpdate: value => {
				filters.value.features = value;
				updateFilters(filters.value);
			}
		},
		{
			id: 'status',
			label: 'Status',
			keys: ['isExclusive'],
			component: PropertyStatus,
			isOpen: false,
			extraProps: { modelValue: filters.value?.isExclusive },
			onUpdate: (exclusivityStatus: boolean) => {
				filters.value.isExclusive = exclusivityStatus;
				updateFilters(filters.value);
			}
		},
		{
			id: 'meter-square-price',
			label: 'Preço por m²',
			keys: ['minSquareMeterPrice', 'maxSquareMeterPrice'],
			component: MeterSquarePrice,
			isOpen: false,
			extraProps: {
				min: filters.value?.minSquareMeterPrice,
				'onUpdate:min': (value: number) => {
					filters.value.minSquareMeterPrice = value;
					updateFilters(filters.value);
				},
				max: filters.value?.maxSquareMeterPrice,
				'onUpdate:max': (value: number) => {
					filters.value.maxSquareMeterPrice = value;
					updateFilters(filters.value);
				}
			},
			onUpdate: () => {
				updateFilters(filters.value);
			}
		},
		{
			id: 'tax-price',
			label: 'IPTU Mensal',
			keys: ['minTax', 'maxTax'],
			component: TaxPrice,
			isOpen: false,
			extraProps: {
				min: filters.value?.minTax,
				'onUpdate:min': (value: number) => {
					filters.value.minTax = value;
					updateFilters(filters.value);
				},
				max: filters.value?.maxTax,
				'onUpdate:max': (value: number) => {
					filters.value.maxTax = value;
					updateFilters(filters.value);
				}
			},
			onUpdate: () => {
				updateFilters(filters.value);
			}
		},
		{
			id: 'monthly-condo-price',
			label: 'Condomínio mensal',
			keys: ['minCondoFee', 'maxCondoFee'],
			component: MensalCondoPrice,
			isOpen: false,
			extraProps: {
				min: filters.value?.minCondoFee,
				'onUpdate:min': (value: number) => {
					filters.value.minCondoFee = value;
					updateFilters(filters.value);
				},
				max: filters.value?.maxCondoFee,
				'onUpdate:max': (value: number) => {
					filters.value.maxCondoFee = value;
					updateFilters(filters.value);
				}
			},
			onUpdate: () => {
				updateFilters(filters.value);
			}
		}
	]);

	const numberOfAppliedClosedFilters = computed<number>(() =>
		filterComponents.value.reduce((number, component) => {
			if (component.isOpen) return number;

			const appliedFiltersPerComponent = appliedFilterKeys.value.filter(key =>
				component.keys.includes(key)
			).length;

			return number + appliedFiltersPerComponent;
		}, 0)
	);

	return {
		filtersBarOpenFilters,
		appliedFilterKeys,
		filterComponents,
		numberOfAppliedClosedFilters
	};
}
